import { ButtonFlavor, ButtonSize, ButtonType, ExButton, ExIcon } from '@boomi/exosphere';
import { At } from '@phosphor-icons/react';
import Heading, { type Level } from '@tiptap/extension-heading';
import Link, { type LinkProtocolOptions } from '@tiptap/extension-link';
import Mention from '@tiptap/extension-mention';
import {
    type Editor,
    EditorContent,
    type Extensions,
    ReactRenderer,
    useEditor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback } from 'react';
import { Markdown } from 'tiptap-markdown';
import type { TriggerFieldInputProps } from '../../../types/types';
import { newGuid } from '../../../utils/utils';
import tippy, { type Instance } from 'tippy.js';
import MentionList from './MentionList';

export type MentionSuggestion = {
    id: string;
    label: string;
    description: string;
};

const MarkdownField = ({ option, value, onChange, outputs }: TriggerFieldInputProps) => {
    const linkConfig = {
        defaultProtocol: 'https',
        protocols: ['http', 'https'],
        isAllowedUri: (
            url: string,
            ctx: {
                defaultValidate: (url: string) => boolean;
                protocols: Array<LinkProtocolOptions | string>;
                defaultProtocol: string;
            },
        ) => {
            try {
                // construct URL
                const parsedUrl = url.includes(':')
                    ? new URL(url)
                    : new URL(`${ctx.defaultProtocol}://${url}`);

                // use default validation
                if (!ctx.defaultValidate(parsedUrl.href)) {
                    return false;
                }

                // disallowed protocols
                const disallowedProtocols = ['ftp', 'file', 'mailto'];
                const protocol = parsedUrl.protocol.replace(':', '');

                if (disallowedProtocols.includes(protocol)) {
                    return false;
                }

                // only allow protocols specified in ctx.protocols
                const allowedProtocols = ctx.protocols.map((p) =>
                    typeof p === 'string' ? p : p.scheme,
                );

                if (!allowedProtocols.includes(protocol)) {
                    return false;
                }

                // all checks have passed
                return true;
            } catch {
                return false;
            }
        },
    };

    const suggestionItems = (query: string) =>
        outputs
            ?.map((output) => ({
                id: newGuid(),
                label: output.name,
                description: output.description,
            }))
            .filter((item: MentionSuggestion) =>
                item.label.toLowerCase().startsWith(query.toLowerCase()),
            ) ?? [];

    const extensions: Extensions = [
        StarterKit,
        Link.configure(linkConfig),
        Markdown.configure({ html: true }),
        Heading,
    ];

    if (option.allowOutputs) {
        extensions.push(
            Mention.configure({
                HTMLAttributes: {
                    class: 'tiptap-mention',
                },
                deleteTriggerWithBackspace: true,
                suggestion: {
                    items: ({ query }: { query: string }) => {
                        return suggestionItems(query);
                    },
                    render: () => {
                        let component: ReactRenderer;
                        let popup: Instance[];

                        return {
                            onStart: (props) => {
                                const { clientRect } = props;

                                if (!props.editor || !clientRect) {
                                    return;
                                }

                                component = new ReactRenderer(MentionList, {
                                    props,
                                    editor: props.editor,
                                });

                                popup = tippy('body', {
                                    getReferenceClientRect: () => clientRect() ?? new DOMRect(),
                                    appendTo: () => document.body,
                                    content: component.element,
                                    showOnCreate: true,
                                    interactive: true,
                                    trigger: 'manual',
                                    placement: 'bottom-start',
                                });
                            },

                            onUpdate(props) {
                                component.updateProps(props);

                                const { clientRect } = props;

                                if (!clientRect) {
                                    return;
                                }

                                popup[0].setProps({
                                    getReferenceClientRect: () => clientRect() ?? new DOMRect(),
                                });
                            },

                            onExit() {
                                popup[0].destroy();
                                component.destroy();
                            },
                        };
                    },
                },
            }),
        );
    }

    const editor = useEditor({
        extensions,
        content: value?.toString() ?? '',
        onUpdate: ({ editor }) => onChange(option.id, editor.storage['markdown'].getMarkdown()),
    });

    const setLink = useCallback(() => {
        if (!editor) {
            return;
        }

        const previousUrl = editor.getAttributes('link')['href'];
        const url = window.prompt('URL', previousUrl);

        // cancelled
        if (url === null) {
            return;
        }

        // empty
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();

            return;
        }

        // update link
        try {
            editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: `//${url}` })
                .run();
        } catch (e) {
            alert((e as Error).message);
        }
    }, [editor]);

    if (!editor) {
        return null;
    }

    const getFixedMenu = (editor: Editor) => (
        <div className="menu-wrapper">
            <div className="menu-main-control-group">
                <select
                    className="menu-select menu-item-first"
                    value={editor.getAttributes('heading')['level'] ?? 0}
                    onChange={(e) => {
                        const val = Number(e.target.value);
                        if (val === 0) {
                            editor.chain().focus().setNode('paragraph').run();
                            return;
                        }
                        editor
                            .chain()
                            .focus()
                            .toggleHeading({ level: Number(e.target.value) as Level })
                            .run();
                    }}
                >
                    <option value={0}>Default</option>
                    <option value={1}>Heading level 1</option>
                    <option value={2}>Heading level 2</option>
                    <option value={3}>Heading level 3</option>
                    <option value={4}>Heading level 4</option>
                    <option value={5}>Heading level 5</option>
                    <option value={6}>Heading level 6</option>
                </select>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className={`menu-button  ${editor.isActive('bold') && 'menu-button-is-active'}`}
                >
                    <ExIcon icon="Bold" />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className={`menu-button ${editor.isActive('italic') && 'menu-button-is-active'}`}
                >
                    <ExIcon icon="Italic" />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleStrike().run()}
                    className={`menu-button ${editor.isActive('strike') && 'menu-button-is-active'}`}
                >
                    <ExIcon icon="Strikethrough" />
                </button>
                <div className="menu-item-break" />
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className={`menu-button ${
                        editor.isActive('bulletList') && 'menu-button-is-active'
                    }`}
                >
                    <ExIcon icon="Bullet list" />
                </button>
                <button
                    type="button"
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    className={`menu-button ${
                        editor.isActive('orderedList') && 'menu-button-is-active'
                    }`}
                >
                    <ExIcon icon="Numbered list" />
                </button>
                <button
                    type="button"
                    onClick={() => setLink()}
                    className={`menu-button ${editor.isActive('link') && 'menu-button-is-active'}`}
                >
                    <ExIcon icon="Link" />
                </button>
            </div>
            {option.allowOutputs && (
                <ExButton
                    className="menu-mentions-button"
                    flavor={ButtonFlavor.BASE}
                    size={ButtonSize.DEFAULT}
                    type={ButtonType.TERTIARY}
                    onClick={() => editor.chain().focus().insertContent('@').run()}
                >
                    <At size={24} />
                </ExButton>
            )}
        </div>
    );

    return (
        <>
            {getFixedMenu(editor)}
            <EditorContent editor={editor} />
        </>
    );
};

export default MarkdownField;
